import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import SyncIcon from "@material-ui/icons/Sync";
import SaveIcon from "@material-ui/icons/Save";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { modelsForProvider, defaultModelFor } from "../../helpers/llmModels";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 760 },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  field: { marginBottom: theme.spacing(2) },
  help: { color: theme.palette.text.secondary, fontSize: 12, marginTop: -8, marginBottom: theme.spacing(2) },
  sectionTitle: { fontWeight: 700, marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  actions: { display: "flex", gap: theme.spacing(2), marginTop: theme.spacing(3), flexWrap: "wrap" },
  syncInfo: { marginTop: theme.spacing(1), fontSize: 13, color: theme.palette.text.secondary },
}));

const EMPTY = {
  enabled: false,
  dryRun: true,
  llmProvider: "openai",
  llmModel: "",
  inventorySourceType: "tokko",
  gvaId: "",
  ttlMinutes: 120,
  tokkoKeySet: false,
  llmKeySet: false,
  lastSyncAt: null,
};

const SuperAdminMatchProp = () => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [form, setForm] = useState(EMPTY);
  // *Edit = lo que el superadmin tipea (vacío = no cambiar al guardar).
  const [llmKey, setLlmKey] = useState("");
  const [tokkoKey, setTokkoKey] = useState("");
  // *Reveal = key en claro traída del backend SOLO para ver (NO se envía en PUT).
  const [revealed, setRevealed] = useState({ llm: null, tokko: null });
  const [revealing, setRevealing] = useState({ llm: false, tokko: false });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const t = (k, opts) => i18n.t(`superMatchProp.${k}`, opts);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/dashboard");
        setCompanies(data.companies || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const fetchConfig = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setLlmKey("");
    setTokkoKey("");
    setRevealed({ llm: null, tokko: null });
    setRevealing({ llm: false, tokko: false });
    try {
      const { data } = await api.get(`/superadmin/companies/${id}/matchprop`);
      setForm({ ...EMPTY, ...data });
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  const handleSelectCompany = (id) => {
    setCompanyId(id);
    fetchConfig(id);
  };

  const sf = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        dryRun: form.dryRun,
        llmProvider: form.llmProvider,
        llmModel: form.llmModel,
        inventorySourceType: form.inventorySourceType,
        ttlMinutes: form.ttlMinutes,
      };
      if (form.inventorySourceType === "gva") payload.gvaId = (form.gvaId || "").trim();
      if (llmKey.trim()) payload.llmKey = llmKey.trim();
      if (tokkoKey.trim()) payload.tokkoKey = tokkoKey.trim();
      const { data } = await api.put(`/superadmin/companies/${companyId}/matchprop`, payload);
      setForm({ ...EMPTY, ...data });
      setLlmKey("");
      setTokkoKey("");
      setRevealed({ llm: null, tokko: null });
      setRevealing({ llm: false, tokko: false });
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleSync = async () => {
    if (!companyId) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/superadmin/companies/${companyId}/matchprop/sync`);
      setForm((f) => ({ ...f, lastSyncAt: data.lastSyncAt }));
      toast.success(t("syncOk", { count: data.count }));
    } catch (err) {
      toastError(err);
    }
    setSyncing(false);
  };

  const keyPlaceholder = (isSet) =>
    isSet ? t("keyConfigured") : t("keyNotSet");

  // Revela (GET puntual) u oculta una key. Revelar NO marca edición: la key en
  // claro vive en `revealed`, separada del valor editable (*Key). Solo se envía
  // en el PUT si el superadmin tipeó en el campo editable.
  const toggleReveal = async (which) => {
    if (revealing[which]) {
      setRevealing((r) => ({ ...r, [which]: false }));
      setRevealed((v) => ({ ...v, [which]: null }));
      return;
    }
    try {
      const { data } = await api.get(
        `/superadmin/companies/${companyId}/matchprop/reveal-key`,
        { params: { which } }
      );
      setRevealed((v) => ({ ...v, [which]: data.key || "" }));
      setRevealing((r) => ({ ...r, [which]: true }));
    } catch (err) {
      toastError(err);
    }
  };

  // Render de un campo de key con ojo (revelar/ocultar). `editVal`/`setEdit`
  // controlan el valor editable; `which` mapea a revealed/revealing y al backend.
  const renderKeyField = (label, which, editVal, setEdit, isSet) => {
    const isRevealing = revealing[which];
    const revealedVal = revealed[which];
    return (
      <TextField
        label={label}
        type={isRevealing ? "text" : "password"}
        variant="outlined"
        fullWidth
        size="small"
        className={classes.field}
        value={isRevealing ? (revealedVal || "") : editVal}
        onChange={(e) => !isRevealing && setEdit(e.target.value)}
        placeholder={keyPlaceholder(isSet)}
        autoComplete="new-password"
        InputProps={{
          readOnly: isRevealing,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                title={isRevealing ? t("hideKey") : t("showKey")}
                onClick={() => toggleReveal(which)}
                disabled={!isSet}
              >
                {isRevealing ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5">{t("title")}</Typography>
      <Typography className={classes.help}>{t("subtitle")}</Typography>

      <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
        <InputLabel>{t("company")}</InputLabel>
        <Select
          value={companyId}
          onChange={(e) => handleSelectCompany(e.target.value)}
          label={t("company")}
        >
          <MenuItem value=""><em>{t("selectCompany")}</em></MenuItem>
          {companies.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name} (ID {c.id})</MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && <CircularProgress size={28} />}

      {!loading && companyId && (
        <Paper className={classes.paper}>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.enabled}
                onChange={(e) => sf("enabled")(e.target.checked)}
                color="primary"
              />
            }
            label={t("enabled")}
          />
          <div className={classes.help}>{t("enabledHelp")}</div>

          <FormControlLabel
            control={
              <Switch
                checked={!!form.dryRun}
                onChange={(e) => sf("dryRun")(e.target.checked)}
                color="primary"
              />
            }
            label={t("dryRun")}
          />
          <div className={classes.help}>{t("dryRunHelp")}</div>

          <Divider />
          <Typography className={classes.sectionTitle}>{t("llmSection")}</Typography>

          <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
            <InputLabel>{t("llmProvider")}</InputLabel>
            <Select
              value={form.llmProvider}
              onChange={(e) => {
                const provider = e.target.value;
                setForm((f) => {
                  const models = modelsForProvider(provider);
                  const llmModel = models.includes(f.llmModel) ? f.llmModel : defaultModelFor(provider);
                  return { ...f, llmProvider: provider, llmModel };
                });
              }}
              label={t("llmProvider")}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="anthropic">Anthropic</MenuItem>
              <MenuItem value="gemini">Gemini</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
            <InputLabel>{t("llmModel")}</InputLabel>
            <Select
              value={form.llmModel || ""}
              onChange={(e) => sf("llmModel")(e.target.value)}
              label={t("llmModel")}
            >
              {/* valor guardado fuera del catálogo → mostrarlo igual */}
              {form.llmModel && !modelsForProvider(form.llmProvider).includes(form.llmModel) && (
                <MenuItem value={form.llmModel}>{form.llmModel}</MenuItem>
              )}
              {modelsForProvider(form.llmProvider).map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {renderKeyField(t("llmKey"), "llm", llmKey, setLlmKey, form.llmKeySet)}

          <Divider />
          <Typography className={classes.sectionTitle}>{t("inventorySection")}</Typography>

          <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
            <InputLabel>{t("inventorySource")}</InputLabel>
            <Select
              value={form.inventorySourceType}
              onChange={(e) => sf("inventorySourceType")(e.target.value)}
              label={t("inventorySource")}
            >
              <MenuItem value="tokko">Tokko</MenuItem>
              <MenuItem value="gva">GVA</MenuItem>
            </Select>
          </FormControl>

          {form.inventorySourceType === "gva" ? (
            <>
              <TextField
                label={t("gvaId")}
                variant="outlined"
                fullWidth
                size="small"
                className={classes.field}
                value={form.gvaId || ""}
                onChange={(e) => sf("gvaId")(e.target.value)}
              />
              <div className={classes.help}>{t("gvaHelp")}</div>
              {/* GVA token shares config.encryptedKey with Tokko → which="tokko" */}
              {renderKeyField(t("gvaToken"), "tokko", tokkoKey, setTokkoKey, form.tokkoKeySet)}
            </>
          ) : (
            renderKeyField(t("tokkoKey"), "tokko", tokkoKey, setTokkoKey, form.tokkoKeySet)
          )}

          <TextField
            label={t("ttlMinutes")}
            type="number"
            variant="outlined"
            fullWidth
            size="small"
            className={classes.field}
            value={form.ttlMinutes}
            onChange={(e) => sf("ttlMinutes")(e.target.value)}
          />

          {form.lastSyncAt && (
            <div className={classes.syncInfo}>
              {t("lastSync")}: {new Date(form.lastSyncAt).toLocaleString()}
            </div>
          )}

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              onClick={handleSave}
            >
              {t("save")}
            </Button>
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              disabled={syncing || !form.enabled}
              onClick={handleSync}
            >
              {t("forceSync")}
            </Button>
          </div>
        </Paper>
      )}
    </div>
  );
};

export default SuperAdminMatchProp;
